import React, { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import { Typography } from '@mui/material';

import RoomTypeCapacities from '~/components/Vendors/Properties/RoomTypeCapacities';

import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';
import { roomRateAvailabilityQueryKeys } from '~/hooks/apis/reservation/roomRateAvailabilityQueryKeys';
import { useGetRoomTypeCapacities } from '~/hooks/apis/reservation/useGetRoomTypeCapacities';

import ReservationService from '~/services/ReservationService';

import { deleteAllInChunks } from '~/utils/deleteAllInChunks';

import { getPartnerSubmittedRoomAsset } from '../../utils';

import MaximumCapacitiesSummary from './MaximumCapacitiesSummary';

interface Props {
  propertyId: string;
  channelManager?: string;
  roomType: App.RoomType;
  roomRate: App.RoomRate;
}

export default function CapacitiesTab({ propertyId, channelManager, roomType, roomRate }: Props) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { data: offer } = useGetOffer(offerId);
  const { data: opportunityRelated } = useGetOpportunityRelated(offer?.id_salesforce_external);
  const partnerSubmittedRoomAsset = getPartnerSubmittedRoomAsset(roomType.name, opportunityRelated?.deals);

  const { data: capacities = [] } = useGetRoomTypeCapacities(propertyId, roomType.id, roomRate.id);

  function invalidate() {
    queryClient.invalidateQueries({
      queryKey: roomRateAvailabilityQueryKeys.capacities(propertyId, roomType.id, roomRate.id),
    });
  }

  async function handleDeleteAll() {
    setIsDeletingAll(true);
    const ids = capacities.map((capacity) => capacity.id);
    await deleteAllInChunks(
      ids,
      (id) => ReservationService.deleteRoomTypeCapacity(id, propertyId, roomType.id, roomRate.id),
      (error) => enqueueSnackbar(`Failed to delete all capacities: ${error.message}`, { variant: 'error' }),
    );
    setIsDeletingAll(false);
    invalidate();
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Maximum capacities come from the partner-submitted room form. Add the combinations this room rate sells below —
        each must fit inside those maxima.
      </Typography>

      <MaximumCapacitiesSummary roomAsset={partnerSubmittedRoomAsset} offerId={offerId} />

      <RoomTypeCapacities
        propertyId={propertyId}
        channelManager={channelManager}
        roomType={roomType}
        roomRate={roomRate}
        capacities={capacities}
        deleteRoomTypeCapacity={async (id: string) => {
          await ReservationService.deleteRoomTypeCapacity(id, propertyId, roomType.id, roomRate.id);
          invalidate();
        }}
        onUpdateRoomTypeCapacities={invalidate}
        isDeletingAll={isDeletingAll}
        onDeleteAll={handleDeleteAll}
      />
    </>
  );
}
